import { useState, useEffect, useCallback } from 'react';
import { s, authHeaders, buildODataUrl, Toast, StandardDataGrid, gridActionButton, getODataRows, resolveODataTotalCount } from './listPageStyles';

const API_BASE = '/api/invoices';

const EMPTY_FORM = {
  textId: '', printedNo: '', printedType: '', receiptNo: '', erpId: '', specialId: '',
  customerId: '', representativeId: '', vendorId: '', warehouseId: '',
  deliveryAdress: '', description1: '', description2: '',
  documentDate: '', dueDate: '', shipDate: '', dueDay: '', year: '',
  documentType: '', paymentType: '', paymentTypeId: '', eArchive: '', status: '',
  grossAmount: '', netAmount: '', discountAmount: '',
  discountPercentage1: '', discountPercentage2: '', discountPercentage3: '',
  vatBase: '', vatAmount: '', specialConsumptionTaxAmount: '',
  orderId: '', dispatchId: '',
};

const EMPTY_DETAIL = {
  id: '',
  clientId: '',
  productId: '',
  warehouseId: '',
  quantity: '1',
  unitOrder: '',
  unitPrice: '',
  unitNetPrice: '',
  unitVatPrice: '',
  grossAmount: '',
  netAmount: '',
  discountAmount: '',
  discountPercentage1: '',
  discountPercentage2: '',
  discountPercentage3: '',
  discountPercentage4: '',
  discountPercentage5: '',
  discountPercentage6: '',
  discountPercentage7: '',
  discountPercentage8: '',
  vatBase: '',
  vatAmount: '',
  vatPercentage: '20',
};

const LOOKUP_SORT_FIELDS = ['CustomerLabel', 'RepresentativeLabel', 'VendorLabel'];

function unwrapLookupItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.Data)) return payload.Data;
  if (Array.isArray(payload?.value)) return payload.value;
  return [];
}

function toInt(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function toNullableInt(value) {
  if (value === '' || value == null) return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function toDecimal(value) {
  const parsed = Number.parseFloat(String(value || '').replace(',', '.'));
  return Number.isNaN(parsed) ? 0 : parsed;
}

function toNullableGuid(value) {
  return value || null;
}

function toNullableDate(value) {
  return value ? new Date(value).toISOString() : null;
}

function dateInputValue(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function compareText(a, b, direction) {
  const left = (a || '').toString().toLocaleLowerCase('tr-TR');
  const right = (b || '').toString().toLocaleLowerCase('tr-TR');
  const result = left.localeCompare(right, 'tr');
  return direction === 'asc' ? result : -result;
}

const moneyFormatter = new Intl.NumberFormat('tr-TR', {
  style: 'currency',
  currency: 'TRY',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatMoney(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? moneyFormatter.format(parsed) : '-';
}

function createClientId() {
  return window.crypto?.randomUUID?.() || `detail-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function decimalString(value) {
  const parsed = toDecimal(value);
  return parsed ? String(Number(parsed.toFixed(2))) : '';
}

function recalcDetail(row) {
  const quantity = toDecimal(row.quantity);
  const unitPrice = toDecimal(row.unitPrice);
  const discountAmount = toDecimal(row.discountAmount);
  const vatPercentage = toDecimal(row.vatPercentage);
  const grossAmount = quantity * unitPrice;
  const vatBase = Math.max(0, grossAmount - discountAmount);
  const vatAmount = vatBase * vatPercentage / 100;
  const netAmount = vatBase + vatAmount;
  return {
    ...row,
    grossAmount: decimalString(grossAmount),
    vatBase: decimalString(vatBase),
    vatAmount: decimalString(vatAmount),
    netAmount: decimalString(netAmount),
    unitVatPrice: quantity ? decimalString(vatAmount / quantity) : '',
    unitNetPrice: quantity ? decimalString(netAmount / quantity) : '',
  };
}

function displayDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('tr-TR');
}

function formatPercent(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed)
    ? `%${Math.round(parsed).toLocaleString('tr-TR')}`
    : '-';
}

function normalizeGuid(value) {
  return String(value || '').trim().toLowerCase();
}

const PAYMENT_TYPE_CODE_BY_TEXT_ID = {
  NAKIT: 1,
  VPOS: 2,
  HAVALE: 3,
  EFT: 3,
  CEK: 4,
  CHECK: 4,
  DBS: 5,
};

function normalizePaymentTypeTextId(value) {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replaceAll('İ', 'I')
    .replaceAll('Ş', 'S')
    .replaceAll('Ğ', 'G')
    .replaceAll('Ü', 'U')
    .replaceAll('Ö', 'O')
    .replaceAll('Ç', 'C')
    .replaceAll('/', '')
    .replaceAll('-', '')
    .replaceAll('_', '')
    .replaceAll(' ', '');
}

function resolvePaymentTypeCode(item) {
  const normalizedTextId = normalizePaymentTypeTextId(item?.textId);
  if (Object.prototype.hasOwnProperty.call(PAYMENT_TYPE_CODE_BY_TEXT_ID, normalizedTextId)) {
    return PAYMENT_TYPE_CODE_BY_TEXT_ID[normalizedTextId];
  }
  const parsed = Number.parseInt(item?.textId, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function pickValue(source, ...keys) {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null) return value;
  }
  return null;
}

function getCollectionSummary(invoiceTotal, paidTotal, payments, dueDate) {
  const safeInvoiceTotal = Math.max(0, toDecimal(invoiceTotal));
  const safePaidTotal = Math.max(0, toDecimal(paidTotal));
  const due = dueDate ? new Date(dueDate) : null;
  const isPastDue = due && !Number.isNaN(due.getTime()) && due < new Date();
  const percent = safeInvoiceTotal > 0
    ? Math.max(0, Math.min(100, (safePaidTotal / safeInvoiceTotal) * 100))
    : 0;
  const lastPaymentDate = (payments || []).reduce((latest, payment) => {
    const value = pickValue(payment, 'paymentDate', 'PaymentDate');
    if (!value) return latest;
    const current = new Date(value);
    if (Number.isNaN(current.getTime())) return latest;
    if (!latest) return current;
    return current > latest ? current : latest;
  }, null);

  if (safeInvoiceTotal > 0 && safePaidTotal >= safeInvoiceTotal) {
    return { label: 'Tahsil Edildi', percent, lastPaymentDate };
  }

  if (safePaidTotal > 0) {
    return { label: 'Kismi Tahsilat', percent, lastPaymentDate };
  }

  if (isPastDue) {
    return { label: 'Vadesi Gecmis', percent: 0, lastPaymentDate: null };
  }

  return { label: 'Beklemede', percent: 0, lastPaymentDate: null };
}

const invoiceEditorStyles = {
  shell: {
    width: 'min(1280px, calc(100vw - 36px))',
    maxHeight: '92vh',
    overflow: 'auto',
    background: '#f3f6f8',
    borderRadius: 8,
    boxShadow: '0 24px 70px rgba(15, 23, 42, 0.32)',
    color: '#111827',
  },
  header: {
    margin: 18,
    marginBottom: 14,
    padding: 20,
    borderRadius: 8,
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  },
  eyebrow: {
    margin: 0,
    color: '#6b7280',
    fontSize: 13,
    fontWeight: 700,
  },
  title: {
    margin: '4px 0 0',
    color: '#111827',
    fontSize: 26,
    fontWeight: 800,
    letterSpacing: 0,
  },
  subtitle: {
    margin: '8px 0 0',
    color: '#4b5563',
    fontSize: 13,
    maxWidth: 620,
  },
  headerActions: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  body: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 2fr) minmax(300px, 0.9fr)',
    gap: 18,
    padding: '0 18px 18px',
  },
  mainColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    minWidth: 0,
  },
  sideColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    minWidth: 0,
  },
  section: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 18,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 14,
  },
  sectionTitle: {
    margin: 0,
    fontSize: 17,
    fontWeight: 800,
    color: '#111827',
  },
  statusBadge: {
    borderRadius: 8,
    background: '#fef3c7',
    color: '#92400e',
    padding: '5px 10px',
    fontSize: 12,
    fontWeight: 800,
  },
  grid2: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 14,
  },
  grid3: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 14,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    color: '#6b7280',
    fontSize: 12,
    fontWeight: 800,
  },
  input: {
    ...s.input,
    borderRadius: 8,
    background: '#f9fafb',
    minHeight: 42,
  },
  inputView: {
    ...s.inputView,
    borderRadius: 8,
    minHeight: 42,
  },
  select: {
    ...s.select,
    borderRadius: 8,
    background: '#f9fafb',
    minHeight: 42,
  },
  textarea: {
    ...s.input,
    borderRadius: 8,
    background: '#f9fafb',
    minHeight: 86,
    resize: 'vertical',
  },
  lineTable: {
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    overflow: 'hidden',
  },
  lineHeader: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr',
    gap: 8,
    padding: '10px 12px',
    background: '#f9fafb',
    color: '#6b7280',
    fontSize: 12,
    fontWeight: 800,
    textTransform: 'uppercase',
  },
  lineRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr',
    gap: 8,
    padding: '12px',
    borderTop: '1px solid #e5e7eb',
    color: '#111827',
    fontSize: 13,
  },
  summaryRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '8px 0',
    color: '#4b5563',
    fontSize: 13,
  },
  summaryStrong: {
    borderTop: '1px solid #e5e7eb',
    marginTop: 6,
    paddingTop: 14,
    color: '#111827',
    fontSize: 18,
    fontWeight: 900,
  },
  notePanel: {
    background: '#18233a',
    color: '#ffffff',
    borderRadius: 8,
    padding: '24px 22px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 18,
  },
  notePanelInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    minWidth: 0,
  },
  notePanelEyebrow: {
    margin: 0,
    color: 'rgba(255,255,255,0.8)',
    fontSize: 13,
    fontWeight: 500,
  },
  notePanelTitle: {
    margin: 0,
    color: '#ffffff',
    fontSize: 24,
    fontWeight: 900,
    lineHeight: 1.15,
  },
  notePanelMeta: {
    margin: 0,
    color: 'rgba(255,255,255,0.82)',
    fontSize: 13,
  },
  notePanelBadge: {
    flexShrink: 0,
    borderRadius: 999,
    background: 'rgba(255,255,255,0.12)',
    color: '#ffffff',
    padding: '10px 16px',
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1,
    whiteSpace: 'nowrap',
  },
};

export function InvoicesPageLegacy() {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);

  const [customers, setCustomers] = useState([]);
  const [representatives, setRepresentatives] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [invoiceDetails, setInvoiceDetails] = useState([]);
  const [deletedDetailIds, setDeletedDetailIds] = useState([]);

  const [sortField, setSortField] = useState('DocumentDate');
  const [sortDir, setSortDir] = useState('asc');
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [amountDetailsOpen, setAmountDetailsOpen] = useState(false);
  const [paymentsByInvoice, setPaymentsByInvoice] = useState({});
  const [paymentPopup, setPaymentPopup] = useState(null);

  const labelBy = useCallback((items, id, fields) => {
    const item = items.find(x => x.id === id);
    if (!item) return '-';
    const code = item.textId || item.erpId || '';
    const name = fields.map(field => item[field]).find(Boolean) || '';
    return name ? `${code} - ${name}`.trim().replace(/^- /, '') : code || '-';
  }, []);

  const getCustomerLabel = useCallback((id) => labelBy(customers, id, ['title', 'shortName', 'person']), [customers, labelBy]);
  const getRepresentativeLabel = useCallback((id) => labelBy(representatives, id, ['name']), [representatives, labelBy]);
  const getVendorLabel = useCallback((id) => labelBy(vendors, id, ['name']), [vendors, labelBy]);
  const getWarehouseLabel = useCallback((id) => labelBy(warehouses, id, ['name', 'description']), [warehouses, labelBy]);
  const getProductLabel = useCallback((id) => labelBy(products, id, ['name', 'shortName', 'description']), [products, labelBy]);
  const getPaymentTypeLabel = useCallback((id) => labelBy(paymentTypes, id, ['description']), [paymentTypes, labelBy]);

  const paymentTypeIdByCode = useCallback((code) => {
    const normalizedCode = Number.parseInt(code, 10);
    if (Number.isNaN(normalizedCode)) return '';
    const item = paymentTypes.find(entry => resolvePaymentTypeCode(entry) === normalizedCode);
    return item?.id || '';
  }, [paymentTypes]);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/customers/Select', { headers }).then(res => res.ok ? res.json() : []),
      fetch('/api/representatives/Select', { headers }).then(res => res.ok ? res.json() : []),
      fetch('/api/vendors/Select', { headers }).then(res => res.ok ? res.json() : []),
      fetch('/api/warehouses/Select', { headers }).then(res => res.ok ? res.json() : []),
      fetch('/api/products/Select', { headers }).then(res => res.ok ? res.json() : []),
      fetch('/api/paymenttypes/Select', { headers }).then(res => res.ok ? res.json() : []),
    ]).then(([customerData, representativeData, vendorData, warehouseData, productData, paymentTypeData]) => {
      setCustomers(unwrapLookupItems(customerData));
      setRepresentatives(unwrapLookupItems(representativeData));
      setVendors(unwrapLookupItems(vendorData));
      setWarehouses(unwrapLookupItems(warehouseData));
      setProducts(unwrapLookupItems(productData));
      setPaymentTypes(unwrapLookupItems(paymentTypeData));
    }).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let filterParams = '';
      if (filterText.trim()) {
        const q = filterText.trim().replace(/'/g, "''").toLowerCase();
        filterParams = `(contains(tolower(TextId),'${q}') or contains(tolower(PrintedNo),'${q}') or contains(tolower(ReceiptNo),'${q}') or contains(tolower(ErpId),'${q}') or contains(tolower(Description1),'${q}') or contains(tolower(Description2),'${q}'))`;
      }

      const isLookupSort = LOOKUP_SORT_FIELDS.includes(sortField);
      const url = buildODataUrl(API_BASE, {
        pageSize: isLookupSort ? 100000 : pageSize,
        currentPage: isLookupSort ? 1 : currentPage,
        sortField: isLookupSort ? 'DocumentDate' : sortField,
        sortDir,
        filterParams,
      });
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('Veri alinamadi');
      const data = await res.json();
      const allRows = getODataRows(data);

      if (isLookupSort) {
        const skip = (currentPage - 1) * pageSize;
        const sortedRows = [...allRows].sort((a, b) => {
          const labels = {
            CustomerLabel: [getCustomerLabel(a.customerId), getCustomerLabel(b.customerId)],
            RepresentativeLabel: [getRepresentativeLabel(a.representativeId), getRepresentativeLabel(b.representativeId)],
            VendorLabel: [getVendorLabel(a.vendorId), getVendorLabel(b.vendorId)],
            WarehouseLabel: [getWarehouseLabel(a.warehouseId), getWarehouseLabel(b.warehouseId)],
          };
          return compareText(labels[sortField][0], labels[sortField][1], sortDir);
        });
        setRows(sortedRows.slice(skip, skip + pageSize));
        setTotalCount(sortedRows.length);
      } else {
        setRows(allRows);
        setTotalCount(await resolveODataTotalCount({ data, rows: allRows, currentPage, pageSize, base: API_BASE, filterParams, headers: authHeaders() }));
      }
    } catch (error) {
      showToast('error', error.message || 'Veri alinirken hata olustu');
    } finally {
      setLoading(false);
    }
  }, [filterText, sortField, sortDir, pageSize, currentPage, getCustomerLabel, getRepresentativeLabel, getVendorLabel, getWarehouseLabel]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const ids = rows.map(row => row.id).filter(Boolean);
    if (!ids.length) {
      setPaymentsByInvoice({});
      return;
    }
    const idSet = new Set(ids.map(normalizeGuid));
    const url = `/api/payments?$top=5000&$orderby=${encodeURIComponent('PaymentDate desc')}`;
    let cancelled = false;
    fetch(url, { headers: authHeaders() })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (cancelled || !data) return;
        const payments = getODataRows(data);
        const map = {};
        payments.forEach(payment => {
          const invoiceId = normalizeGuid(pickValue(payment, 'invoiceId', 'InvoiceId'));
          if (!invoiceId || !idSet.has(invoiceId)) return;
          if (!map[invoiceId]) map[invoiceId] = { total: 0, items: [] };
          map[invoiceId].total += toDecimal(pickValue(payment, 'amount', 'Amount'));
          map[invoiceId].items.push(payment);
        });
        setPaymentsByInvoice(map);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [rows]);

  useEffect(() => {
    if (!paymentTypes.length || form.paymentTypeId || form.paymentType === '' || modal == null) return;
    const mappedPaymentTypeId = paymentTypeIdByCode(form.paymentType);
    if (!mappedPaymentTypeId) return;
    setForm(current => current.paymentTypeId ? current : { ...current, paymentTypeId: mappedPaymentTypeId });
  }, [paymentTypes, form.paymentTypeId, form.paymentType, modal, paymentTypeIdByCode]);

  const rowToForm = (row) => ({
    textId: row.textId || '',
    printedNo: row.printedNo || '',
    printedType: row.printedType ?? '',
    receiptNo: row.receiptNo || '',
    erpId: row.erpId || '',
    specialId: row.specialId || '',
    customerId: row.customerId || '',
    representativeId: row.representativeId || '',
    vendorId: row.vendorId || '',
    warehouseId: row.warehouseId || '',
    deliveryAdress: row.deliveryAdress || '',
    description1: row.description1 || '',
    description2: row.description2 || '',
    documentDate: dateInputValue(row.documentDate),
    dueDate: dateInputValue(row.dueDate),
    shipDate: dateInputValue(row.shipDate),
    dueDay: row.dueDay ?? '',
    year: row.year ?? '',
    documentType: row.documentType ?? '',
    paymentType: row.paymentType ?? '',
    paymentTypeId: paymentTypeIdByCode(row.paymentType),
    eArchive: row.eArchive ?? '',
    status: row.status ?? '',
    grossAmount: row.grossAmount ?? '',
    netAmount: row.netAmount ?? '',
    discountAmount: row.discountAmount ?? '',
    discountPercentage1: row.discountPercentage1 ?? '',
    discountPercentage2: row.discountPercentage2 ?? '',
    discountPercentage3: row.discountPercentage3 ?? '',
    vatBase: row.vatBase ?? '',
    vatAmount: row.vatAmount ?? '',
    specialConsumptionTaxAmount: row.specialConsumptionTaxAmount ?? '',
    orderId: row.orderId || '',
    dispatchId: row.dispatchId || '',
  });

  const rowToDetail = (row) => ({
    id: row.id || '',
    clientId: row.id || createClientId(),
    productId: row.productId || '',
    warehouseId: row.warehouseId || '',
    quantity: row.quantity ?? '',
    unitOrder: row.unitOrder ?? '',
    unitPrice: row.unitPrice ?? '',
    unitNetPrice: row.unitNetPrice ?? '',
    unitVatPrice: row.unitVatPrice ?? '',
    grossAmount: row.grossAmount ?? '',
    netAmount: row.netAmount ?? '',
    discountAmount: row.discountAmount ?? '',
    discountPercentage1: row.discountPercentage1 ?? '',
    discountPercentage2: row.discountPercentage2 ?? '',
    discountPercentage3: row.discountPercentage3 ?? '',
    discountPercentage4: row.discountPercentage4 ?? '',
    discountPercentage5: row.discountPercentage5 ?? '',
    discountPercentage6: row.discountPercentage6 ?? '',
    discountPercentage7: row.discountPercentage7 ?? '',
    discountPercentage8: row.discountPercentage8 ?? '',
    vatBase: row.vatBase ?? '',
    vatAmount: row.vatAmount ?? '',
    vatPercentage: row.vatPercentage ?? '20',
  });

  const fetchInvoiceDetails = async (invoiceId) => {
    if (!invoiceId) return [];
    // #795 Kalem sırası LineOrder'dadır (#757/#758); UnitOrder birim slotudur.
    const url = `/api/invoicedetails?$filter=${encodeURIComponent(`InvoiceId eq ${invoiceId}`)}&$orderby=${encodeURIComponent('LineOrder asc,Id asc')}`;
    const res = await fetch(url, { headers: authHeaders() });
    if (!res.ok) throw new Error('Fatura kalemleri alinamadi');
    const data = await res.json();
    return getODataRows(data).map(rowToDetail);
  };

  const openNew = () => {
    const now = new Date();
    setForm({ ...EMPTY_FORM, documentDate: dateInputValue(now), year: now.getFullYear().toString() });
    setInvoiceDetails([]);
    setDeletedDetailIds([]);
    setAmountDetailsOpen(false);
    setSelected(null);
    setModal('new');
  };
  const openEdit = async (row = selected) => {
    if (!row) return;
    setSelected(row);
    setForm(rowToForm(row));
    setDeletedDetailIds([]);
    setAmountDetailsOpen(false);
    setModal('edit');
    try {
      setInvoiceDetails(await fetchInvoiceDetails(row.id));
    } catch (error) {
      showToast('error', error.message || 'Fatura kalemleri alinamadi');
    }
  };
  const openView = async (row = selected) => {
    if (!row) return;
    setSelected(row);
    setForm(rowToForm(row));
    setDeletedDetailIds([]);
    setAmountDetailsOpen(false);
    setModal('view');
    try {
      setInvoiceDetails(await fetchInvoiceDetails(row.id));
    } catch (error) {
      showToast('error', error.message || 'Fatura kalemleri alinamadi');
    }
  };
  const closeModal = () => setModal(null);

  const detailTotals = invoiceDetails.reduce((acc, row) => ({
    grossAmount: acc.grossAmount + toDecimal(row.grossAmount),
    netAmount: acc.netAmount + toDecimal(row.netAmount),
    discountAmount: acc.discountAmount + toDecimal(row.discountAmount),
    vatBase: acc.vatBase + toDecimal(row.vatBase),
    vatAmount: acc.vatAmount + toDecimal(row.vatAmount),
  }), { grossAmount: 0, netAmount: 0, discountAmount: 0, vatBase: 0, vatAmount: 0 });
  const hasDetailRows = invoiceDetails.length > 0;
  const effectiveTotals = {
    grossAmount: hasDetailRows ? detailTotals.grossAmount : toDecimal(form.grossAmount),
    netAmount: hasDetailRows ? detailTotals.netAmount : toDecimal(form.netAmount),
    discountAmount: hasDetailRows ? detailTotals.discountAmount : toDecimal(form.discountAmount),
    vatBase: hasDetailRows ? detailTotals.vatBase : toDecimal(form.vatBase),
    vatAmount: hasDetailRows ? detailTotals.vatAmount : toDecimal(form.vatAmount),
    specialConsumptionTaxAmount: toDecimal(form.specialConsumptionTaxAmount),
  };

  const buildPayload = () => {
    const selectedPaymentType = paymentTypes.find(item => item.id === form.paymentTypeId);
    const resolvedPaymentTypeCode = resolvePaymentTypeCode(selectedPaymentType);

    return {
      id: selected?.id || '00000000-0000-0000-0000-000000000000',
      textId: form.textId,
      printedNo: form.printedNo,
      printedType: toInt(form.printedType),
      receiptNo: form.receiptNo || null,
      erpId: form.erpId || null,
      specialId: form.specialId || null,
      customerId: form.customerId,
      representativeId: form.representativeId,
      vendorId: form.vendorId,
      warehouseId: form.warehouseId,
      deliveryAdress: form.deliveryAdress || null,
      description1: form.description1 || null,
      description2: form.description2 || null,
      documentDate: toNullableDate(form.documentDate),
      dueDate: toNullableDate(form.dueDate),
      shipDate: toNullableDate(form.shipDate),
      dueDay: toNullableInt(form.dueDay),
      year: toInt(form.year),
      documentType: toInt(form.documentType),
      paymentType: resolvedPaymentTypeCode ?? toInt(form.paymentType),
      paymentTypeId: toNullableGuid(form.paymentTypeId),
      eArchive: toInt(form.eArchive),
      status: toInt(form.status),
      grossAmount: effectiveTotals.grossAmount,
      netAmount: effectiveTotals.netAmount,
      discountAmount: effectiveTotals.discountAmount,
      discountPercentage1: toDecimal(form.discountPercentage1),
      discountPercentage2: toDecimal(form.discountPercentage2),
      discountPercentage3: toDecimal(form.discountPercentage3),
      vatBase: effectiveTotals.vatBase,
      vatAmount: effectiveTotals.vatAmount,
      specialConsumptionTaxAmount: toDecimal(form.specialConsumptionTaxAmount),
      orderId: toNullableGuid(form.orderId),
      dispatchId: toNullableGuid(form.dispatchId),
    };
  };

  const buildDetailPayload = (row, index) => ({
    id: row.id || '00000000-0000-0000-0000-000000000000',
    discountAmount: toDecimal(row.discountAmount),
    discountPercentage1: toDecimal(row.discountPercentage1),
    discountPercentage2: toDecimal(row.discountPercentage2),
    discountPercentage3: toDecimal(row.discountPercentage3),
    discountPercentage4: toDecimal(row.discountPercentage4),
    discountPercentage5: toDecimal(row.discountPercentage5),
    discountPercentage6: toDecimal(row.discountPercentage6),
    discountPercentage7: toDecimal(row.discountPercentage7),
    discountPercentage8: toDecimal(row.discountPercentage8),
    grossAmount: toDecimal(row.grossAmount),
    invoiceId: selected?.id || '00000000-0000-0000-0000-000000000000',
    netAmount: toDecimal(row.netAmount),
    productId: row.productId,
    quantity: toDecimal(row.quantity),
    unitNetPrice: toDecimal(row.unitNetPrice),
    unitOrder: toInt(row.unitOrder) || index + 1,
    unitPrice: toDecimal(row.unitPrice),
    unitVatPrice: toDecimal(row.unitVatPrice),
    vatAmount: toDecimal(row.vatAmount),
    vatBase: toDecimal(row.vatBase),
    vatPercentage: toDecimal(row.vatPercentage),
    warehouseId: row.warehouseId || form.warehouseId,
  });

  const addDetailRow = () => {
    setInvoiceDetails(current => ([
      ...current,
      {
        ...EMPTY_DETAIL,
        clientId: createClientId(),
        warehouseId: form.warehouseId || '',
        unitOrder: String(current.length + 1),
      },
    ]));
  };

  const updateDetailRow = (clientId, key, value) => {
    setInvoiceDetails(current => current.map(row => {
      if (row.clientId !== clientId) return row;
      const next = { ...row, [key]: value };
      return ['quantity', 'unitPrice', 'discountAmount', 'vatPercentage'].includes(key) ? recalcDetail(next) : next;
    }));
    if (key === 'productId' && value) {
      fetchEffectivePrice(clientId, value);
    }
  };

  // Ürün seçilince /api/prices/Effective'den salePrice + vatPercentage doldur.
  // Fiyat tanımsızsa kullanıcıyı uyar — manuel giriş şart.
  const fetchEffectivePrice = async (clientId, productId) => {
    if (!productId) return;
    try {
      const params = new URLSearchParams({ productId });
      if (form.customerId) params.append('customerId', form.customerId);
      if (form.vendorId) params.append('vendorId', form.vendorId);
      const res = await fetch(`/api/prices/Effective?${params}`, { headers: authHeaders() });
      if (!res.ok) return;
      const price = await res.json();
      if (!price) {
        showToast('error', 'Bu ürün için tanımlı fiyat yok — manuel giriniz');
        return;
      }
      setInvoiceDetails(current => current.map(row => {
        if (row.clientId !== clientId) return row;
        const next = {
          ...row,
          unitPrice: String(price.salePrice ?? 0),
          vatPercentage: String(price.vatPercentage ?? 0),
        };
        return recalcDetail(next);
      }));
    } catch {
      // sessiz: ağ/sunucu hatasında manuel giriş hakkı zaten var
    }
  };

  const removeDetailRow = (row) => {
    if (row.id) setDeletedDetailIds(current => current.includes(row.id) ? current : [...current, row.id]);
    setInvoiceDetails(current => current.filter(item => item.clientId !== row.clientId));
  };

  const handleSave = async () => {
    if (!form.textId.trim() || !form.printedNo.trim()) {
      showToast('error', 'Fatura kodu ve basili no zorunludur');
      return;
    }
    if (!form.customerId || !form.representativeId || !form.vendorId || !form.warehouseId) {
      showToast('error', 'Musteri, temsilci, tedarikci ve depo secimi zorunludur');
      return;
    }
    if (!form.paymentTypeId) {
      showToast('error', 'Odeme tipi secimi zorunludur');
      return;
    }
    const invalidDetail = invoiceDetails.find(row => !row.productId || !(row.warehouseId || form.warehouseId) || toDecimal(row.quantity) <= 0);
    if (invalidDetail) {
      showToast('error', 'Kalemlerde urun, depo ve miktar zorunludur');
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/SaveWithDetails`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          invoice: buildPayload(),
          details: invoiceDetails.map(buildDetailPayload),
          deletedDetailIds,
        }),
      });
      if (!res.ok) throw new Error('Fatura ve kalemleri kaydedilemedi');
      showToast('success', modal === 'new' ? 'Fatura ve kalemleri eklendi' : 'Fatura ve kalemleri guncellendi');
      closeModal();
      setSelected(null);
      setInvoiceDetails([]);
      setDeletedDetailIds([]);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Islem sirasinda hata olustu');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: selected.id }) });
      if (!res.ok) throw new Error('Kayit silinemedi');
      showToast('success', 'Fatura silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme islemi sirasinda hata olustu');
      setDeleteConfirm(false);
    }
  };

  const isView = modal === 'view';
  const setValue = (key, value) => setForm(current => ({ ...current, [key]: value }));
  const selectedInvoicePayments = selected?.id
    ? paymentsByInvoice[normalizeGuid(selected.id)]
    : null;
  const collectionSummary = getCollectionSummary(
    effectiveTotals.netAmount,
    selectedInvoicePayments?.total || 0,
    selectedInvoicePayments?.items || [],
    form.dueDate,
  );
  const collectionSummaryMeta = collectionSummary.lastPaymentDate
    ? `Son odeme: ${displayDate(collectionSummary.lastPaymentDate)}`
    : 'Son odeme kaydi yok';

  const columns = [
    { field: 'textId', headerName: 'Fatura Kodu', flex: 1, minWidth: 150 },
    { field: 'printedNo', headerName: 'Basili No', flex: 1, minWidth: 140, renderCell: (params) => params.row.printedNo || '-' },
    {
      field: 'documentDate',
      headerName: 'Tarih',
      width: 130,
      renderCell: (params) => params.row.documentDate ? new Date(params.row.documentDate).toLocaleDateString('tr-TR') : '-',
    },
    {
      field: 'customerLabel',
      headerName: 'Musteri',
      flex: 1.3,
      minWidth: 190,
      sortable: false,
      renderCell: (params) => getCustomerLabel(params.row.customerId),
    },
    {
      field: 'representativeLabel',
      headerName: 'Temsilci',
      flex: 1.1,
      minWidth: 170,
      sortable: false,
      renderCell: (params) => getRepresentativeLabel(params.row.representativeId),
    },
    {
      field: 'paymentTypeLabel',
      headerName: 'Odeme Tipi',
      flex: 1,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => getPaymentTypeLabel(paymentTypeIdByCode(params.row.paymentType)),
    },
    {
      field: 'netAmount',
      headerName: 'Net',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <div style={{ width: '100%', textAlign: 'right' }}>
          {formatMoney(params.row.netAmount)}
        </div>
      ),
    },
    {
      field: 'paymentProgress',
      headerName: 'Odeme %',
      width: 190,
      sortable: false,
      filterable: false,
      renderHeader: () => (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span>Odeme %</span>
          <span
            title="Odeme detaylarini gormek icin odeme barina tiklayin."
            aria-label="Odeme detaylari yardimi"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 16,
              height: 16,
              borderRadius: '50%',
              border: '1px solid #cbd5e1',
              color: '#64748b',
              fontSize: 10,
              fontWeight: 800,
              background: '#f8fafc',
              cursor: 'help',
            }}
          >
            i
          </span>
        </div>
      ),
      renderCell: (params) => {
        const net = toDecimal(params.row.netAmount);
        const info = paymentsByInvoice[normalizeGuid(params.row.id)];
        const paid = info?.total || 0;
        const rawPercent = net > 0 ? (paid / net) * 100 : 0;
        const percent = Math.max(0, Math.min(100, rawPercent));
        const displayedPercent = Math.round(rawPercent);
        const statusColor = displayedPercent >= 100 ? '#16a34a' : displayedPercent >= 50 ? '#d97706' : '#dc2626';
        const trackColor = displayedPercent >= 100 ? '#dcfce7' : displayedPercent >= 50 ? '#fef3c7' : '#fee2e2';
        return (
          <div style={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                setPaymentPopup({ invoice: params.row, payments: info?.items || [], total: paid });
              }}
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                height: 28,
                padding: '0 8px',
                border: 'none',
                borderRadius: 9999,
                background: trackColor,
                overflow: 'hidden',
                boxShadow: 'inset 0 0 0 1px rgba(148, 163, 184, 0.28)',
                cursor: 'pointer',
                textAlign: 'left',
              }}
              title="Odeme detaylarini goster"
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: `${percent}%`,
                  borderRadius: 9999,
                  background: `linear-gradient(90deg, ${statusColor} 0%, ${statusColor}CC 100%)`,
                  transition: 'width 0.2s ease',
                }}
              />
              <span
                style={{
                  position: 'relative',
                  zIndex: 1,
                  color: statusColor,
                  fontSize: 11,
                  fontWeight: 800,
                  background: 'rgba(255,255,255,0.88)',
                  border: '1px solid rgba(255,255,255,0.92)',
                  borderRadius: 9999,
                  padding: '1px 6px',
                  lineHeight: 1.2,
                }}
              >
                {`%${displayedPercent}`}
              </span>
              <span
                style={{
                  position: 'relative',
                  zIndex: 1,
                  color: '#111827',
                  fontSize: 11,
                  fontWeight: 700,
                  textShadow: '0 1px 0 rgba(255,255,255,0.35)',
                }}
              >
                {formatMoney(paid)}
              </span>
            </button>
          </div>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Islemler',
      width: 250,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', height: '100%' }}>
          <button
            style={{ ...gridActionButton, background: 'var(--accent)' }}
            onClick={(event) => { event.stopPropagation(); openView(params.row); }}
          >
            Goruntule
          </button>
          <button
            style={{ ...gridActionButton, background: 'var(--warn)' }}
            onClick={(event) => { event.stopPropagation(); openEdit(params.row); }}
          >
            Degistir
          </button>
          <button
            style={{ ...gridActionButton, background: '#E53E3E', minWidth: 48 }}
            onClick={(event) => { event.stopPropagation(); setSelected(params.row); setDeleteConfirm(true); }}
          >
            Sil
          </button>
        </div>
      ),
    },
  ];

  const editorField = (label, key, type = 'text', options = {}) => (
    <div style={{ ...invoiceEditorStyles.field, ...(options.style || {}) }}>
      <label style={invoiceEditorStyles.label}>{label}</label>
      {options.multiline ? (
        <textarea
          style={isView ? { ...invoiceEditorStyles.textarea, ...invoiceEditorStyles.inputView } : invoiceEditorStyles.textarea}
          value={form[key]}
          onChange={e => setValue(key, e.target.value)}
          readOnly={isView}
        />
      ) : (
        <input
          type={type}
          style={isView ? invoiceEditorStyles.inputView : { ...invoiceEditorStyles.input, textAlign: options.align || 'left' }}
          value={form[key]}
          onChange={e => setValue(key, e.target.value)}
          readOnly={isView}
        />
      )}
    </div>
  );

  const editorSelect = (label, key, items, getLabel) => (
    <div style={invoiceEditorStyles.field}>
      <label style={invoiceEditorStyles.label}>{label}</label>
      {isView ? (
        <input style={invoiceEditorStyles.inputView} readOnly value={getLabel(form[key])} />
      ) : (
        <select style={invoiceEditorStyles.select} value={form[key]} onChange={e => setValue(key, e.target.value)}>
          <option value="">Seciniz</option>
          {items.map(item => <option key={item.id} value={item.id}>{getLabel(item.id)}</option>)}
        </select>
      )}
    </div>
  );

  const summaryRow = (label, value, strong = false) => (
    <div style={strong ? { ...invoiceEditorStyles.summaryRow, ...invoiceEditorStyles.summaryStrong } : invoiceEditorStyles.summaryRow}>
      <span>{label}</span>
      <strong style={{ color: strong ? '#111827' : '#111827', fontSize: strong ? 18 : 14 }}>{value}</strong>
    </div>
  );

  return (
    <div style={s.container}>
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h2 style={s.pageTitle}>Faturalar</h2>
          <p style={s.pageSubtitle}>Fatura kayitlarini filtreleyin, siralayin ve yonetin.</p>
        </div>
        <button style={s.btnPrimary} onClick={openNew}>+ Yeni Fatura</button>
      </div>

      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          <input
            style={s.filterInput}
            type="text"
            placeholder="Fatura kodu, no, ERP veya aciklama ile filtrele..."
            value={filterInput}
            onChange={e => setFilterInput(e.target.value)}
            onKeyDown={event => { if (event.key === 'Enter') { setFilterText(filterInput); setCurrentPage(1); } }}
          />
          {filterText && <button style={s.btnSecondary} onClick={() => { setFilterText(''); setFilterInput(''); setCurrentPage(1); }}>Temizle</button>}
          <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
        </div>
      </div>

      <StandardDataGrid
        rows={rows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={page => setCurrentPage(page)}
        onPageSizeChange={size => { setPageSize(size); setCurrentPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={(field, direction) => {
          setCurrentPage(1);
          setSortField(field || 'DocumentDate');
          setSortDir(direction || 'asc');
        }}
        selectedId={selected?.id}
        onRowClick={row => setSelected(row)}
        onRowDoubleClick={row => openView(row)}
        exportFileName="faturalar"
      />

      {modal && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={invoiceEditorStyles.shell} onClick={e => e.stopPropagation()}>
            <div style={invoiceEditorStyles.header}>
              <div>
                <p style={invoiceEditorStyles.eyebrow}>Fatura Yonetimi</p>
                <h3 style={invoiceEditorStyles.title}>{modal === 'new' ? 'Yeni Fatura' : modal === 'edit' ? 'Fatura Duzenle' : 'Fatura Detay'}</h3>
                <p style={invoiceEditorStyles.subtitle}>Musteri bilgilerini, fatura tarihlerini ve tutar detaylarini tek ekranda duzenleyin.</p>
              </div>
              <div style={invoiceEditorStyles.headerActions}>
                {!isView && <button style={s.btnPrimary} onClick={handleSave}>Kaydet</button>}
                <button style={s.btnSecondary} onClick={closeModal}>{isView ? 'Kapat' : 'Iptal'}</button>
              </div>
            </div>

            <div style={invoiceEditorStyles.body}>
              <div style={invoiceEditorStyles.mainColumn}>
                <section style={invoiceEditorStyles.section}>
                  <div style={invoiceEditorStyles.sectionHeader}>
                    <h4 style={invoiceEditorStyles.sectionTitle}>Fatura Bilgileri</h4>
                  </div>
                  <div style={invoiceEditorStyles.grid3}>
                    {editorField('Fatura Kodu *', 'textId')}
                    {editorField('Basili No *', 'printedNo')}
                    {editorField('Makbuz No', 'receiptNo')}
                    {editorField('Fatura Tarihi', 'documentDate', 'date')}
                    {editorField('Vade Tarihi', 'dueDate', 'date')}
                    {editorField('Sevk Tarihi', 'shipDate', 'date')}
                    {editorField('Vade Gunu', 'dueDay')}
                    {editorSelect('Odeme Tipi *', 'paymentTypeId', paymentTypes, getPaymentTypeLabel)}
                  </div>
                </section>

                <section style={invoiceEditorStyles.section}>
                  <h4 style={{ ...invoiceEditorStyles.sectionTitle, marginBottom: 14 }}>Musteri ve Operasyon Bilgileri</h4>
                  <div style={invoiceEditorStyles.grid2}>
                    {editorSelect('Musteri *', 'customerId', customers, getCustomerLabel)}
                    {editorSelect('Temsilci *', 'representativeId', representatives, getRepresentativeLabel)}
                    {editorSelect('Tedarikci *', 'vendorId', vendors, getVendorLabel)}
                    {editorSelect('Depo *', 'warehouseId', warehouses, getWarehouseLabel)}
                  </div>
                </section>

                <section style={invoiceEditorStyles.section}>
                  <div style={invoiceEditorStyles.sectionHeader}>
                    <h4 style={invoiceEditorStyles.sectionTitle}>Fatura Kalemleri</h4>
                    {!isView && <button type="button" style={s.btnSecondary} onClick={addDetailRow}>+ Kalem Ekle</button>}
                  </div>
                  <div style={invoiceEditorStyles.lineTable}>
                    <div style={{ ...invoiceEditorStyles.lineHeader, gridTemplateColumns: '2fr 0.8fr 1fr 1fr 74px' }}>
                      <div>Urun / Hizmet</div>
                      <div style={{ textAlign: 'right' }}>Miktar</div>
                      <div style={{ textAlign: 'right' }}>Birim Fiyat</div>
                      <div style={{ textAlign: 'right' }}>Net</div>
                      <div></div>
                    </div>
                    {invoiceDetails.length === 0 ? (
                      <div style={{ padding: 16, color: '#6b7280', fontSize: 13 }}>Kalem bulunamadi.</div>
                    ) : invoiceDetails.map(row => (
                      <div key={row.clientId} style={{ ...invoiceEditorStyles.lineRow, gridTemplateColumns: '2fr 0.8fr 1fr 1fr 74px', alignItems: 'center' }}>
                        <div>
                          {isView ? (
                            <span>{getProductLabel(row.productId)}</span>
                          ) : (
                            <select style={invoiceEditorStyles.select} value={row.productId} onChange={e => updateDetailRow(row.clientId, 'productId', e.target.value)}>
                              <option value="">Urun seciniz</option>
                              {products.map(item => <option key={item.id} value={item.id}>{getProductLabel(item.id)}</option>)}
                            </select>
                          )}
                        </div>
                        <input type="text" style={{ ...invoiceEditorStyles.input, textAlign: 'right' }} value={row.quantity} readOnly={isView} onChange={e => updateDetailRow(row.clientId, 'quantity', e.target.value)} />
                        <input type="text" style={{ ...invoiceEditorStyles.input, textAlign: 'right' }} value={row.unitPrice} readOnly={isView} onChange={e => updateDetailRow(row.clientId, 'unitPrice', e.target.value)} />
                        <div style={{ textAlign: 'right', fontWeight: 800 }}>{formatMoney(row.netAmount)}</div>
                        {!isView && <button type="button" style={s.btnSmallDanger} onClick={() => removeDetailRow(row)}>Sil</button>}
                      </div>
                    ))}
                  </div>
                </section>

                <section style={invoiceEditorStyles.section}>
                  <h4 style={{ ...invoiceEditorStyles.sectionTitle, marginBottom: 14 }}>Ek Bilgiler</h4>
                  <div style={invoiceEditorStyles.grid3}>
                    {editorField('E-Arsiv', 'eArchive')}
                    {editorField('ERP Kodu', 'erpId')}
                    {editorField('Ozel Kod', 'specialId')}
                  </div>
                  <div style={{ ...invoiceEditorStyles.grid2, marginTop: 14 }}>
                    {editorField('Order Id', 'orderId')}
                    {editorField('Dispatch Id', 'dispatchId')}
                  </div>
                  <div style={{ ...invoiceEditorStyles.grid2, marginTop: 14 }}>
                    {editorField('Teslimat Adresi', 'deliveryAdress', 'text', { multiline: true })}
                    {editorField('Aciklama 1', 'description1', 'text', { multiline: true })}
                    {editorField('Aciklama 2', 'description2', 'text', { multiline: true })}
                  </div>
                </section>
              </div>

              <aside style={invoiceEditorStyles.sideColumn}>
                <section style={invoiceEditorStyles.section}>
                  <h4 style={{ ...invoiceEditorStyles.sectionTitle, marginBottom: 14 }}>Ozet</h4>
                  {summaryRow('Ara Toplam', formatMoney(effectiveTotals.grossAmount))}
                  {summaryRow('Iskonto', formatMoney(effectiveTotals.discountAmount))}
                  {summaryRow('KDV Toplami', formatMoney(effectiveTotals.vatAmount))}
                  {summaryRow('OTV', formatMoney(effectiveTotals.specialConsumptionTaxAmount))}
                  {summaryRow('Genel Toplam', formatMoney(effectiveTotals.netAmount), true)}
                </section>

                <section style={invoiceEditorStyles.notePanel}>
                  <div style={invoiceEditorStyles.notePanelInfo}>
                    <p style={invoiceEditorStyles.notePanelEyebrow}>Tahsilat Durumu</p>
                    <h4 style={invoiceEditorStyles.notePanelTitle}>{collectionSummary.label}</h4>
                    <p style={invoiceEditorStyles.notePanelMeta}>{collectionSummaryMeta}</p>
                  </div>
                  <span style={invoiceEditorStyles.notePanelBadge}>{formatPercent(collectionSummary.percent)} Tahsil Edildi</span>
                </section>

                <section style={invoiceEditorStyles.section}>
                  <div style={{ ...invoiceEditorStyles.sectionHeader, marginBottom: amountDetailsOpen ? 14 : 0 }}>
                    <h4 style={invoiceEditorStyles.sectionTitle}>Tutar Detaylari</h4>
                    <button type="button" style={{ ...s.btnSecondary, minWidth: 36, padding: '7px 10px' }} onClick={() => setAmountDetailsOpen(value => !value)} title={amountDetailsOpen ? 'Kapat' : 'Ac'}>
                      {amountDetailsOpen ? '-' : '+'}
                    </button>
                  </div>
                  {amountDetailsOpen && (
                    <div style={invoiceEditorStyles.grid2}>
                      {editorField('Brut Tutar', 'grossAmount', 'text', { align: 'right' })}
                      {editorField('Net Tutar', 'netAmount', 'text', { align: 'right' })}
                      {editorField('Iskonto Tutar', 'discountAmount', 'text', { align: 'right' })}
                      {editorField('KDV Matrah', 'vatBase', 'text', { align: 'right' })}
                      {editorField('KDV Tutar', 'vatAmount', 'text', { align: 'right' })}
                      {editorField('OTV Tutar', 'specialConsumptionTaxAmount', 'text', { align: 'right' })}
                      {editorField('Iskonto %1', 'discountPercentage1', 'text', { align: 'right' })}
                      {editorField('Iskonto %2', 'discountPercentage2', 'text', { align: 'right' })}
                      {editorField('Iskonto %3', 'discountPercentage3', 'text', { align: 'right' })}
                    </div>
                  )}
                </section>
              </aside>
            </div>
          </div>
        </div>
      )}

      {paymentPopup && (
        <div style={s.overlay} onClick={() => setPaymentPopup(null)}>
          <div style={{ ...s.modal, maxWidth: 780 }} onClick={event => event.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Fatura Odemeleri - {paymentPopup.invoice.textId || paymentPopup.invoice.printedNo || '-'}</h3>
              <button style={s.closeBtn} onClick={() => setPaymentPopup(null)}>x</button>
            </div>
            <div style={s.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                <div>
                  <div style={{ color: '#6b7280', fontSize: 12, fontWeight: 700 }}>Fatura Toplami</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>{formatMoney(paymentPopup.invoice.netAmount)}</div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', fontSize: 12, fontWeight: 700 }}>Toplam Odeme</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>{formatMoney(paymentPopup.total)}</div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', fontSize: 12, fontWeight: 700 }}>Kalan</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>
                    {formatMoney(Math.max(0, (Number(paymentPopup.invoice.netAmount) || 0) - paymentPopup.total))}
                  </div>
                </div>
              </div>

              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1.1fr 1fr 0.9fr 1fr', gap: 8, padding: '10px 12px', background: '#f9fafb', color: '#6b7280', fontSize: 12, fontWeight: 800, textTransform: 'uppercase' }}>
                  <div>Odeme No</div>
                  <div>Tarih</div>
                  <div>Banka</div>
                  <div>Makbuz</div>
                  <div style={{ textAlign: 'right' }}>Tutar</div>
                </div>
                {paymentPopup.payments.length === 0 ? (
                  <div style={{ padding: 16, color: '#6b7280', fontSize: 13 }}>Bu faturaya ait odeme kaydi bulunmuyor.</div>
                ) : paymentPopup.payments.map(payment => (
                  <div key={payment.id} style={{ display: 'grid', gridTemplateColumns: '1.1fr 1.1fr 1fr 0.9fr 1fr', gap: 8, padding: '12px', borderTop: '1px solid #e5e7eb', fontSize: 13, color: '#111827' }}>
                    <div>{payment.textId || '-'}</div>
                    <div>{displayDate(payment.paymentDate)}</div>
                    <div>{payment.bankName || '-'}</div>
                    <div>{payment.receiptNo || '-'}</div>
                    <div style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoney(payment.amount)}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnSecondary} onClick={() => setPaymentPopup(null)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayi</h3></div>
            <div style={s.modalBody}><p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>Bu faturayi silmek istediginize emin misiniz?</p></div>
            <div style={s.modalFooter}><button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button><button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>Iptal</button></div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}
